import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import { CapTableDetailsModel, WaterfallService } from 'api';
import { CapTableVersionWithCompanyId, ChildCompany } from 'common/types/company';
import { Fund } from 'common/types/fund';
import useFundInvestmentSheetConfig from 'pages/Waterfall/SpreadsheetConfig/FundInvestmentSheetConfig/useFundInvestmentSheetConfig/useFundInvestmentSheetConfig';
import useWaterfallSheetConfig from 'pages/Waterfall/SpreadsheetConfig/WaterfallSheetConfig/useWaterfallSheetConfig/useWaterfallSheetConfig';
import { ISO_DATE_FORMAT, MOMENT_DEFAULT_DATE_FORMAT, sumArrayValues } from 'utilities';
import calculateOwnersSharesValue from './calculateOwnersSharesValue';
import {
  parseRelatedEntityAdjustments,
  RelatedEntityAdjustments,
  serializeRelatedEntityAdjustments,
} from './relatedEntityAdjustments';
import {
  RelatedEntitiesWaterfalls,
  WeightedShareValue,
} from '../components/FundFirmDistribution/FundFirmDistribution.props';
import { ScenarioWithCompanyId } from '../types';

export type RelatedEntitySheet =
  | NonNullable<ReturnType<typeof useWaterfallSheetConfig>['sheetConfig']>
  | ReturnType<typeof useFundInvestmentSheetConfig>['spreadsheets'][number];

const useRelatedEntitiesWaterfalls = ({
  updatedWeightedShareValues,
  childrenCapTables,
  exitDate,
  fundList,
  childrenCompanies,
  mainFundOwnershipRef,
  relatedEntityAdjustments,
}: {
  updatedWeightedShareValues: WeightedShareValue[] | undefined;
  childrenCapTables: CapTableVersionWithCompanyId[];
  exitDate: string;
  fundList: Fund[];
  childrenCompanies: ChildCompany[];
  mainFundOwnershipRef: React.RefObject<CapTableDetailsModel['fund_ownership'] | null>;
  relatedEntityAdjustments?: RelatedEntityAdjustments;
}) => {
  const [relatedEntitiesWaterfalls, setRelatedEntitiesWaterfalls] = useState<RelatedEntitiesWaterfalls | null>(null);
  const [childrenSheets, setChildrenSheets] = useState<Record<number, RelatedEntitySheet[]>>({});
  const [isRelatedEntitiesWaterfallsLoading, setIsRelatedEntitiesWaterfallsLoading] = useState(false);
  const previousRequestContextKeyRef = useRef<string | null>(null);

  const registerChildrenSheets = useCallback((sheets: RelatedEntitySheet[], idKey: number) => {
    setChildrenSheets(prevState => ({ ...prevState, [idKey]: sheets }));
  }, []);
  const resetChildrenSheets = useCallback(() => {
    setChildrenSheets(prevState => (Object.keys(prevState).length ? {} : prevState));
  }, []);
  const buildRelatedEntitiesWaterfallsMap = (
    results: PromiseSettledResult<ScenarioWithCompanyId>[]
  ): RelatedEntitiesWaterfalls => results.reduce((accum, res) => {
    if (res.status === 'fulfilled') {
      const { companyId, ...rest } = res.value;
      // eslint-disable-next-line no-param-reassign
      accum[companyId] = rest;
    }
    return accum;
  }, {} as RelatedEntitiesWaterfalls);

  const relatedEntitiesEnterpriseValues = useMemo(() => {
    const shareValuesForRelatedEntities = updatedWeightedShareValues?.filter(ws => ws.owner === 'related_entity') ?? [];
    if (shareValuesForRelatedEntities.length === 0) {
      return null;
    }
    return shareValuesForRelatedEntities.reduce(
      (acc, curr) => {
        acc[curr.fund.id] = sumArrayValues(curr.share_values.map(sv => sv.per_share * sv.shares));
        return acc;
      },
      {} as Record<number, number>
    );
  }, [updatedWeightedShareValues]);

  const relatedEntityAdjustmentsKey = serializeRelatedEntityAdjustments(relatedEntityAdjustments ?? {}) ?? '{}';
  const stableRelatedEntityAdjustments = useMemo(
    () => parseRelatedEntityAdjustments(relatedEntityAdjustmentsKey),
    [relatedEntityAdjustmentsKey]
  );

  const relatedEntitiesEquityValues = useMemo(() => {
    if (relatedEntitiesEnterpriseValues == null) {
      return null;
    }
    return Object.entries(relatedEntitiesEnterpriseValues).reduce<Record<number, number>>(
      (equityValues, [relatedEntityIdValue, enterpriseValue]) => {
        const relatedEntityId = Number(relatedEntityIdValue);
        const adjustment = stableRelatedEntityAdjustments[relatedEntityId] ?? { plusCash: 0, lessDebt: 0 };
        // eslint-disable-next-line no-param-reassign
        equityValues[relatedEntityId] = enterpriseValue + adjustment.plusCash - adjustment.lessDebt;
        return equityValues;
      },
      {}
    );
  }, [relatedEntitiesEnterpriseValues, stableRelatedEntityAdjustments]);

  const requestContextKey = useMemo(
    () =>
      JSON.stringify({
        capTables: childrenCapTables
          .map(({ company_id: companyId, id, is_empty: isEmpty }) => ({ companyId, id, isEmpty }))
          .sort((first, second) => Number(first.companyId) - Number(second.companyId)),
        enterpriseValues:
          relatedEntitiesEnterpriseValues == null
            ? null
            : Object.entries(relatedEntitiesEnterpriseValues).sort(
              ([firstRelatedEntityId], [secondRelatedEntityId]) =>
                Number(firstRelatedEntityId) - Number(secondRelatedEntityId)
            ),
        exitDate,
      }),
    [childrenCapTables, exitDate, relatedEntitiesEnterpriseValues]
  );

  // Preserve settled sheets during adjustment-only recalculations so the workbook does not collapse while requests run.
  useEffect(() => {
    const hasRequestContextChanged =
      previousRequestContextKeyRef.current !== null && previousRequestContextKeyRef.current !== requestContextKey;
    previousRequestContextKeyRef.current = requestContextKey;

    if (relatedEntitiesEquityValues == null) {
      resetChildrenSheets();
      setRelatedEntitiesWaterfalls(null);
      setIsRelatedEntitiesWaterfallsLoading(false);
      return undefined;
    }

    let isCurrentRequest = true;
    const requestsData = Object.entries(relatedEntitiesEquityValues)
      .map(([relatedEntityId, equityValue]) => {
        const capTable = childrenCapTables.find(ct => ct.company_id === Number(relatedEntityId));
        if (!capTable || !Number.isFinite(equityValue) || capTable.is_empty) {
          return null;
        }
        return {
          capTable: Number(capTable.id),
          // Cash/Debt stay unchanged in URL state; only the calculation defaults to zero.
          equityValue: Math.max(equityValue, 0),
          isEmpty: capTable.is_empty,
          companyId: Number(relatedEntityId),
        };
      })
      .filter(requestData => requestData !== null);
    const parsedExitDate = moment(exitDate, [MOMENT_DEFAULT_DATE_FORMAT, ISO_DATE_FORMAT], true);
    if (!parsedExitDate.isValid() || requestsData.length === 0) {
      resetChildrenSheets();
      setRelatedEntitiesWaterfalls({});
      setIsRelatedEntitiesWaterfallsLoading(false);
      return undefined;
    }
    const exitDateString = parsedExitDate.format(ISO_DATE_FORMAT);

    if (hasRequestContextChanged) {
      // Never show results from another company, cap table, exit date, or enterprise value.
      resetChildrenSheets();
      setRelatedEntitiesWaterfalls(null);
    }
    setIsRelatedEntitiesWaterfallsLoading(true);

    const requestAndSetRelatedEntitiesWaterfalls = async () => {
      // `/api/waterfall` always runs liquidation waterfall math per cap table.
      const requestsToRun = requestsData.map(data =>
        WaterfallService.apiWaterfallCreate({
          captableId: data.capTable,
          equityValue: data.equityValue.toString(),
          exitDate: exitDateString,
        }).then(result => ({ ...result, companyId: data.companyId }))
      ) as Promise<ScenarioWithCompanyId>[];

      const results = await Promise.allSettled(requestsToRun);
      const relatedEntitiesWaterfallsMap = buildRelatedEntitiesWaterfallsMap(results);
      if (isCurrentRequest) {
        const resolvedRelatedEntityIds = new Set(Object.keys(relatedEntitiesWaterfallsMap).map(Number));
        setChildrenSheets(previousSheets => {
          const retainedEntries = Object.entries(previousSheets).filter(([relatedEntityId]) =>
            resolvedRelatedEntityIds.has(Number(relatedEntityId))
          );

          return retainedEntries.length === Object.keys(previousSheets).length
            ? previousSheets
            : Object.fromEntries(retainedEntries);
        });
        setRelatedEntitiesWaterfalls(relatedEntitiesWaterfallsMap);
        setIsRelatedEntitiesWaterfallsLoading(false);
      }
    };

    requestAndSetRelatedEntitiesWaterfalls();
    return () => {
      isCurrentRequest = false;
    };
  }, [childrenCapTables, exitDate, relatedEntitiesEquityValues, requestContextKey, resetChildrenSheets]);

  const ownersShareValuesMap = useMemo(() => {
    if (!relatedEntitiesWaterfalls) {
      return null;
    }
    const auxMap = {} as Record<number, WeightedShareValue[]>;
    Object.entries(relatedEntitiesWaterfalls).forEach(([relatedEntityId, waterfallData]) => {
      const capTable = childrenCapTables.find(ct => ct.company_id === Number(relatedEntityId));
      if (!capTable?.id) {
        return;
      }
      const sortedPresentValues = orderBy(waterfallData.present_values, ['security.id'], ['desc']);
      const ownerSharesValue = calculateOwnersSharesValue({
        sortedPresentValues,
        fundList,
        capTableDetail: {
          id: capTable.id,
          securities: (capTable.securities ?? []).map(({ id, name }) => ({ id, name })),
        },
        childrenCapTables,
        childrenCompanies,
        mainFundOwnershipRef,
        entityReferenceId: Number(relatedEntityId),
      });
      auxMap[Number(relatedEntityId)] = ownerSharesValue;
    });
    return auxMap;
  }, [relatedEntitiesWaterfalls, fundList, childrenCapTables, childrenCompanies, mainFundOwnershipRef]);

  return {
    ownersShareValuesMap,
    relatedEntitiesWaterfalls,
    isRelatedEntitiesWaterfallsLoading,
    childrenSheets,
    registerChildrenSheets,
    resetChildrenSheets,
    relatedEntitiesEnterpriseValues,
    relatedEntitiesEquityValues,
  };
};

export default useRelatedEntitiesWaterfalls;
